import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import IntroSection from "../components/IntroSection";
import IntroSectionSkeleton from "../components/IntroSectionSkeleton";
import { api, formatError } from "../lib/api";
import { useIntro } from "../hooks/useIntro";
import { toast } from "sonner";
import {
  Award, Save, Send, ClipboardCheck, Sparkles, Star,
  ChevronRight, Lock, Mail, User, Trophy, ArrowRight, Lightbulb,
  Wand2, Loader2, X, FolderPlus, Folder, Check,
} from "lucide-react";
import { BLUEPRINT_SECTIONS, REQUIRED_FIELDS } from "../data/blueprintSections";
import ImagePickerField from "../components/ImagePickerField";

const AUTO_SAVE_MS = 1200;

// Paste ANY of these into videoUrl and IntroSection auto-picks the renderer:
//   • YouTube share URL         "https://youtu.be/XXXXXXX"
//   • YouTube embed URL         "https://www.youtube.com/embed/XXXXXXX"
//   • Vimeo player URL          "https://player.vimeo.com/video/XXXXXXX"
//   • Direct MP4 / WEBM / MOV   "https://cdn.example.com/intro.mp4"
export const BLUEPRINT_INTRO = {
  eyebrow: "Launch Blueprint Intro",
  title: "60 seconds to your first shelf-ready plan.",
  body: "The Blueprint is the one document the CPG Institute panel reads before your review call — brand, category, ops, distribution, and financials, all in one place. Watch the walkthrough, then work section-by-section — every field auto-saves, and the panel score updates as you go.",
  videoUrl: "",
  bullets: [
    "8 short sections · fill in the order that makes sense to you",
    "Auto-save on every keystroke — never lose a paragraph",
    "Panel score refreshes as you edit · reviewed within 5-10 business days",
  ],
};

export default function LaunchBlueprint() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ status: "draft", responses: {} });
  const { intro, ready: introReady } = useIntro("blueprint", BLUEPRINT_INTRO);
  const [activeSection, setActiveSection] = useState(BLUEPRINT_SECTIONS[0].id);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  // Which submit flow the modal is running: "submit" locks the answers
  // in as an immutable panel submission; "draft" only snapshots them
  // to Projects for later promotion. Both use the same folder picker.
  const [saveMode, setSaveMode]           = useState("submit");
  // Set of field ids the founder has edited since the last panel-score refresh.
  // Drives the "3 edits — check your new score" nudge inside <ScorePreview />.
  const [editedFields, setEditedFields] = useState(() => new Set());
  const [searchParams] = useSearchParams();
  const snapshotId = searchParams.get("snapshot");
  // Snapshot mode: the URL carries a `?snapshot=<project_id>` — we
  // load that project's saved responses read-only. Editing is disabled
  // so the founder can compare their saved-then-submitted version to
  // the current live blueprint without accidentally overwriting it.
  const [snapshotMeta, setSnapshotMeta] = useState(null);
  // In snapshot mode we still need to know the LIVE blueprint's status
  // to decide whether a draft snapshot can be submitted right now.
  const [liveStatus, setLiveStatus]     = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (snapshotId) {
      setLoading(true);
      api.get(`/projects/${snapshotId}`)
        .then((r) => {
          const p = r.data || {};
          setSnapshotMeta({
            id: p.id, name: p.name, created_at: p.created_at,
            variant: p.blueprint_variant || "submit",
            comments: p.comments,
          });
          setState({
            status: "snapshot",
            responses: p.blueprint_snapshot || {},
          });
        })
        .catch((err) => {
          toast.error(formatError(err));
          setState({ status: "draft", responses: {} });
        })
        .finally(() => setLoading(false));
      // Independently fetch the live blueprint status so a draft
      // snapshot can decide whether its "Submit this draft" CTA is
      // enabled right now. Errors are non-fatal.
      api.get("/blueprint/me")
        .then((r) => setLiveStatus(r.data?.status || null))
        .catch(() => setLiveStatus(null));
      return;
    }
    // Live-blueprint path — always clear any prior snapshot banner
    // state so returning from a snapshot view doesn't leave the
    // "Read-only snapshot" strip visible on the live page.
    setSnapshotMeta(null);
    api.get("/blueprint/me")
      .then((r) => setState({ status: r.data.status || "draft", responses: r.data.responses || {}, ...r.data }))
      .catch(() => setState({ status: "draft", responses: {} }))
      .finally(() => setLoading(false));
  }, [snapshotId]);

  // Draft snapshots (variant='draft') are editable in-place: auto-save
  // PATCHes the project doc's `blueprint_snapshot`, isolated from the
  // live blueprint doc. Submit snapshots stay read-only.
  const isDraftSnapshot = !!snapshotId && snapshotMeta?.variant === "draft";
  const readOnlySnapshot = !!snapshotId && !isDraftSnapshot;

  // Debounced auto-save whenever responses change
  useEffect(() => {
    if (loading) return;
    if (readOnlySnapshot) return; // submit-variant snapshots are immutable
    if (snapshotId && !snapshotMeta) return; // still hydrating snapshot meta
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        if (isDraftSnapshot) {
          await api.patch(`/projects/${snapshotId}`, {
            blueprint_snapshot: state.responses,
          });
        } else if (state.status === "draft") {
          await api.put("/blueprint/me", { responses: state.responses });
        }
      } catch (err) {
        toast.error(formatError(err));
      } finally { setSaving(false); }
    }, AUTO_SAVE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.responses]);

  const setField = (id, val) => {
    // Submit-variant snapshots are the immutable panel record — never mutate.
    if (readOnlySnapshot) return;
    setState((s) => ({ ...s, responses: { ...s.responses, [id]: val } }));
    // Only track meaningful edits — ignore the declaration checkbox toggle and
    // no-op re-writes (same value).
    setEditedFields((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const clearEdited = () => setEditedFields(new Set());

  const missingRequired = useMemo(
    () => REQUIRED_FIELDS.filter((id) => {
      const v = state.responses[id];
      return v === undefined || v === "" || v === false || v === null;
    }),
    [state.responses],
  );

  // Opens the Save & Submit modal. Actual save + submit runs inside
  // the modal's confirm handler so the founder can pick a folder,
  // create a new one, or drop the snapshot in the root before we
  // fire the panel submit.
  const openSubmitFlow = () => {
    if (missingRequired.length > 0) {
      toast.error(`${missingRequired.length} required field${missingRequired.length === 1 ? "" : "s"} still empty`);
      return;
    }
    setSaveMode("submit");
    setSaveModalOpen(true);
  };

  // Manual save-as-draft — same folder picker, but no panel submission.
  // The auto-save keeps writing to /blueprint/me continuously; this
  // creates an explicit named snapshot in Projects the founder can
  // return to and eventually promote to a submission.
  const openSaveDraftFlow = () => {
    setSaveMode("draft");
    setSaveModalOpen(true);
  };

  const submitDraftSnapshot = async () => {
    // Guard: required-fields must be filled BEFORE the draft can be
    // promoted into a panel submission. Toast surfaces the count so the
    // founder knows what's still missing.
    if (missingRequired.length > 0) {
      toast.error(`${missingRequired.length} required field${missingRequired.length === 1 ? "" : "s"} still empty — fill them in before submitting for review.`);
      return;
    }
    if (!window.confirm("Submit this draft to the CPG Institute panel? Your live blueprint will be replaced with these answers first.")) return;
    setSubmitting(true);
    try {
      // 1. Persist the very latest edits onto the draft project doc so
      //    nothing is lost if the submit call fails mid-flight.
      await api.patch(`/projects/${snapshotId}`, {
        blueprint_snapshot: state.responses,
      });
      // 2. Promote the draft: overwrite the live blueprint with these
      //    answers, then fire the standard panel submit.
      await api.put("/blueprint/me", { responses: state.responses });
      const { data } = await api.post("/blueprint/submit");
      // 3. Flip the project doc variant draft → submit and stamp the
      //    matching submitted_at so the Projects card can pair with
      //    this specific live review (Under review → Score X).
      await api.patch(`/projects/${snapshotId}`, {
        blueprint_variant: "submit",
        blueprint_submitted_at: data?.submitted_at || new Date().toISOString(),
      });
      toast.success("Draft submitted for panel review.");
      nav("/projects");
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const submitAfterSave = async ({ parentId, projectName }) => {
    setSubmitting(true);
    try {
      // Force-save the live blueprint first so nothing is lost, then
      // snapshot the exact responses into a new project item.
      await api.put("/blueprint/me", { responses: state.responses });
      await api.post("/projects", {
        name:               projectName,
        type:               "launch_blueprint",
        blueprint_variant:  saveMode,        // "submit" | "draft"
        parent_id:          parentId || null,
        blueprint_snapshot: state.responses,
      });
      if (saveMode === "submit") {
        const { data } = await api.post("/blueprint/submit");
        setState((s) => ({ ...s, status: data.status, submitted_at: data.submitted_at }));
        toast.success("Saved to Projects and submitted for panel review.");
      } else {
        toast.success("Draft saved to Projects.");
      }
      setSaveModalOpen(false);
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const reopenBlueprint = async () => {
    if (!window.confirm("Start over? Your previous answers will be cleared so you can fill out every Blueprint section from scratch. Your prior review will stay in your history for the panel to see.")) return;
    setReopening(true);
    try {
      await api.post("/blueprint/reopen");
      // Refetch so we get the latest server state including any preserved
      // review under `submission_history` and cleared submitted/reviewed
      // top-level fields. The server also wipes `responses` on reopen so
      // every section starts blank — do NOT fall back to prior answers.
      const { data } = await api.get("/blueprint/me");
      setState({
        status: data.status || "draft",
        responses: data.responses || {},
        ...data,
        // Belt-and-suspenders: ensure the freshly cleared responses win
        // even if the spread above re-introduces a stale field.
        responses: data.responses || {},
      });
      setActiveSection(BLUEPRINT_SECTIONS[0].id);
      toast.success("Fresh start — every section is now blank. Fill it out again and resubmit.");
    } catch (err) { toast.error(formatError(err)); }
    finally { setReopening(false); }
  };

  const isSubmitted = state.status === "submitted" || state.status === "reviewed";
  const isReviewed = state.status === "reviewed";

  if (loading) return <AppShell title="Launch Blueprint"><div className="text-sm text-muted-foreground">Loading…</div></AppShell>;

  // ─────────────────────────────────────────────── Post-submit / reviewed
  if (isSubmitted) {
    return (
      <AppShell title="Launch Blueprint">
        <div className="w-full">
          <div className="grad-band text-white rounded-2xl p-8" data-testid="blueprint-submitted">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 grid place-items-center shrink-0">
                {isReviewed ? <Trophy className="w-7 h-7" /> : <ClipboardCheck className="w-7 h-7" />}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/80 font-semibold">
                  {isReviewed ? "Blueprint reviewed" : "Blueprint submitted for panel review"}
                </p>
                <h1 className="font-display text-3xl md:text-4xl mt-1">
                  {isReviewed ? "Your review is ready." : "Thanks — we've got your blueprint."}
                </h1>
                <p className="mt-2 text-sm md:text-base text-white/90 max-w-lg">
                  {isReviewed
                    ? "The CPG Institute panel scored and left detailed feedback below. Book your 1:1 with Warren Ezra using the link at the end."
                    : "The CPG Institute panel will review your submission. You'll get written feedback within 5–10 business days plus a private strategy session with Warren Ezra, and possibly startup discounts or free services."}
                </p>
              </div>
            </div>
          </div>

          {isReviewed && (
            <>
              <div className="mt-6 grid sm:grid-cols-3 gap-4">
                <StatCard label="Panel score" value={<><span className="font-display text-4xl">{state.review_score}</span><span className="text-muted-foreground text-xl">/100</span></>} />
                <StatCard label="Reviewed by" value={<span className="text-sm font-semibold">{state.reviewer || "CPG Institute Panel"}</span>} />
                <StatCard label="Reviewed on" value={<span className="text-sm">{state.reviewed_at?.slice(0, 10)}</span>} />
              </div>
              <div className="mt-6 surface p-6" data-testid="blueprint-feedback">
                <p className="text-xs uppercase tracking-widest text-primary font-semibold">Panel feedback</p>
                <p className="mt-3 leading-relaxed whitespace-pre-line">{state.review_feedback}</p>
              </div>
              {state.review_action_items && (
                <div className="mt-4 surface p-6" data-testid="blueprint-action-items">
                  <p className="text-xs uppercase tracking-widest text-primary font-semibold">Action items</p>
                  <p className="mt-3 leading-relaxed whitespace-pre-line">{state.review_action_items}</p>
                </div>
              )}
              <div className="mt-6 rounded-2xl border border-shell-accent/40 bg-pink-50 p-6 flex items-start gap-4" data-testid="blueprint-warren-call">
                <Sparkles className="w-6 h-6 text-shell-accent mt-1 shrink-0" />
                <div>
                  <p className="font-display text-xl">Book your 1:1 with Warren Ezra</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Included with every reviewed blueprint. Warren will personally walk through your plan, share tactical next steps, and unlock any startup discounts or free services you're eligible for.
                  </p>
                  <a href="#" className="btn-primary mt-4 inline-flex" data-testid="blueprint-book-warren">
                    <Mail className="w-4 h-4" /> Book a Call
                  </a>
                </div>
              </div>
            </>
          )}

          {/* Try Again — shown ONLY after the panel has completed their
              review. During the submitted-but-not-yet-reviewed stage the
              founder waits; no Edit/Try Again/Resubmit control is offered
              there, matching the original submission confirmation flow. */}
          {isReviewed && (
            <div
              className="mt-6 rounded-2xl border border-shell-border bg-white p-6 flex items-start gap-4"
              data-testid="blueprint-resubmit-card"
            >
              <ArrowRight className="w-6 h-6 text-primary mt-1 shrink-0" />
              <div className="flex-1">
                <p className="font-display text-xl">Want to sharpen your plan and resubmit?</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Open your blueprint back up, apply the panel's feedback, and resubmit for another round. Your previous review is saved to your history.
                </p>
                <button
                  type="button"
                  onClick={reopenBlueprint}
                  disabled={reopening}
                  className="btn-primary mt-4 inline-flex"
                  data-testid="blueprint-reopen-btn"
                >
                  {reopening ? "Reopening…" : (
                    <>Try again <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </AppShell>
    );
  }

  // ─────────────────────────────────────────────── Draft — fill mode
  const totalFields = BLUEPRINT_SECTIONS.reduce((n, s) => n + s.fields.length, 0);
  const filledFields = BLUEPRINT_SECTIONS.reduce(
    (n, s) => n + s.fields.filter((f) => {
      const v = state.responses[f.id];
      return v !== undefined && v !== "" && v !== false && v !== null;
    }).length, 0,
  );
  const pct = Math.round((filledFields / totalFields) * 100);

  return (
    <AppShell title="Launch Blueprint">
      <div className="w-full">
        {/* Intro */}
        {introReady && intro ? (
          <IntroSection
            {...intro}
            ctaLabel="Start with the first section"
            ctaHref="#blueprint-content"
            allowSeek
            configHint={<>Admin → Intros to edit copy + video. Fallback in <code>src/pages/LaunchBlueprint.jsx</code>.</>}
            testId="blueprint-intro"
          />
        ) : (
          <IntroSectionSkeleton testId="blueprint-intro" />
        )}

        <div id="blueprint-content" className="mt-10">
          <PageHero
            eyebrow="Founder Launch Blueprint"
            icon={Award}
            title={<>Your <span className="text-grad">launch plan</span>, reviewed by the panel.</>}
            subtitle="After you complete the modules, you submit this Blueprint to the CPG Institute panel. You'll receive detailed written feedback within 5–10 business days, a private 1:1 with Warren Ezra, and — if your plan is a fit — startup discounts and free services from our partner network."
          />
        </div>

        {/* Panel score preview */}
        <ScorePreview
          filledFields={filledFields}
          editedCount={editedFields.size}
          onScoreRefreshed={clearEdited}
        />

        {snapshotMeta && (
          <div
            className={`mt-6 mb-6 rounded-2xl border px-5 py-4 flex items-start gap-3 ${snapshotMeta.variant === "draft" ? "border-pink-200 bg-pink-50" : "border-primary/20 bg-primary/5"}`}
            data-testid="blueprint-snapshot-banner"
          >
            <Trophy className={`w-5 h-5 shrink-0 mt-0.5 ${snapshotMeta.variant === "draft" ? "text-pink-600" : "text-primary"}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-[10px] uppercase tracking-widest font-semibold ${snapshotMeta.variant === "draft" ? "text-pink-700" : "text-primary"}`}>
                {snapshotMeta.variant === "draft"
                  ? "Saved draft — you can submit this to the panel"
                  : "Saved Launch Blueprint — read-only"}
              </p>
              <p className="font-display text-lg mt-0.5 truncate">{snapshotMeta.name}</p>
              <p className={`text-xs mt-1 ${snapshotMeta.variant === "draft" ? "text-pink-900/80" : "text-muted-foreground"}`}>
                Saved {new Date(snapshotMeta.created_at).toLocaleString()}.
                {snapshotMeta.variant === "draft"
                  ? " You can edit any section — changes auto-save to this draft. When you're ready, submit it for panel review from the final section."
                  : " This view is a snapshot — your live blueprint is unaffected."}
              </p>
            </div>
            <button
              onClick={() => nav("/blueprint")}
              className="text-xs font-semibold text-primary hover:underline shrink-0 self-start"
              data-testid="blueprint-back-to-live"
            >
              Back to live blueprint
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-[240px_1fr] gap-8">
          {/* Section nav rail */}
          <aside className="lg:sticky lg:top-24 h-fit" data-testid="blueprint-nav">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">
              Sections · <span className="text-primary">{pct}% complete</span>
            </p>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-4">
              <div className="h-full bg-gradient-to-r from-primary to-shell-accent transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <nav className="flex flex-col gap-1">
              {BLUEPRINT_SECTIONS.map((s) => {
                const filled = s.fields.filter((f) => state.responses[f.id]).length;
                const done = filled === s.fields.length && s.fields.every((f) => !f.required || state.responses[f.id]);
                const isActive = activeSection === s.id;
                return (
                  <button key={s.id} onClick={() => setActiveSection(s.id)}
                    data-testid={`blueprint-nav-${s.id}`}
                    className={[
                      "text-left rounded-lg px-3 py-2 text-xs flex items-center gap-2 transition-colors",
                      isActive ? "bg-primary/10 text-primary font-semibold" : "text-foreground/80 hover:bg-muted",
                    ].join(" ")}>
                    <span className={[
                      "w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold shrink-0",
                      done ? "bg-green-500 text-white" : isActive ? "bg-primary text-white" : "bg-muted",
                    ].join(" ")}>
                      {done ? "✓" : String(BLUEPRINT_SECTIONS.indexOf(s) + 1)}
                    </span>
                    <span className="flex-1 truncate">{s.title.replace(/^Section \d+ — /, "")}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Section content */}
          <div>
            {BLUEPRINT_SECTIONS.map((s) => (
              <div key={s.id} className={activeSection === s.id ? "" : "hidden"} data-testid={`blueprint-section-${s.id}`}>
                <p className="text-xs uppercase tracking-widest text-primary font-semibold">
                  {s.title.split(" — ")[0]}
                </p>
                <h2 className="font-display text-3xl mt-1">{s.title.split(" — ")[1]}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{s.intro}</p>

                <div className="mt-6 grid gap-5">
                  {s.fields.map((f) => (
                    <BlueprintField key={f.id} field={f}
                      value={state.responses[f.id] ?? (f.type === "checkbox" ? false : "")}
                      onChange={(val) => setField(f.id, val)}
                    />
                  ))}
                </div>

                {/* Prev / next nav — Save Draft is shown on every
                    step (except when viewing a read-only snapshot) so
                    the founder can pause and come back mid-section
                    without hunting for the last step. Submit for
                    panel review stays reserved for the final step. */}
                <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
                  {(() => {
                    const idx = BLUEPRINT_SECTIONS.indexOf(s);
                    const prev = BLUEPRINT_SECTIONS[idx - 1];
                    const next = BLUEPRINT_SECTIONS[idx + 1];
                    // Read-only snapshot views handle their own action
                    // row (submit-draft / wait message / back link).
                    if (snapshotId) {
                      return (
                        <>
                          {prev ? (
                            <button onClick={() => setActiveSection(prev.id)}
                              className="btn-ghost" data-testid={`blueprint-prev-${s.id}`}>
                              ← {prev.title.split(" — ")[1]}
                            </button>
                          ) : <span />}
                          {next ? (
                            <button onClick={() => setActiveSection(next.id)}
                              className="btn-primary" data-testid={`blueprint-next-${s.id}`}>
                              Next: {next.title.split(" — ")[1]} <ChevronRight className="w-4 h-4" />
                            </button>
                          ) : snapshotMeta?.variant === "draft" ? (
                            liveStatus === "submitted" ? (
                              <div
                                className="w-full rounded-lg border border-pink-200 bg-pink-50 px-3 py-2 text-xs text-pink-900 leading-relaxed"
                                data-testid="blueprint-draft-waiting"
                              >
                                Once the submitted data is reviewed and you receive a score, you can submit this draft. Until then, please wait.
                              </div>
                            ) : (
                              <button
                                onClick={submitDraftSnapshot}
                                disabled={submitting}
                                className="btn-primary"
                                data-testid="blueprint-submit-draft"
                              >
                                {submitting ? "Submitting…" : <>Submit this draft for panel review <Send className="w-4 h-4" /></>}
                              </button>
                            )
                          ) : (
                            <span className="text-xs text-muted-foreground italic">Read-only snapshot — return to your live blueprint to edit.</span>
                          )}
                        </>
                      );
                    }
                    // Live edit mode — Save Draft available on every
                    // step; Submit only on the final one.
                    return (
                      <>
                        {prev ? (
                          <button onClick={() => setActiveSection(prev.id)}
                            className="btn-ghost" data-testid={`blueprint-prev-${s.id}`}>
                            ← {prev.title.split(" — ")[1]}
                          </button>
                        ) : <span />}
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <button
                            onClick={openSaveDraftFlow}
                            disabled={submitting}
                            className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-5 py-2.5 text-xs font-semibold text-foreground hover:border-primary hover:text-primary transition"
                            data-testid={`blueprint-save-draft-${s.id}`}
                          >
                            <Save className="w-4 h-4" /> Save draft
                          </button>
                          {next ? (
                            <button onClick={() => setActiveSection(next.id)}
                              className="btn-primary" data-testid={`blueprint-next-${s.id}`}>
                              Next: {next.title.split(" — ")[1]} <ChevronRight className="w-4 h-4" />
                            </button>
                          ) : (
                            <button onClick={openSubmitFlow} disabled={submitting} className="btn-primary" data-testid="blueprint-submit">
                              {submitting ? "Submitting…" : <>Submit for panel review <Send className="w-4 h-4" /></>}
                            </button>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Persistent save indicator */}
        <div className="mt-8 rounded-xl bg-muted/60 border border-shell-border px-4 py-3 flex items-center gap-3 text-xs text-muted-foreground" data-testid="blueprint-save-status">
          <Save className={["w-3.5 h-3.5", saving ? "animate-pulse text-primary" : ""].join(" ")} />
          {saving ? "Saving…" : `Auto-saves after every change. ${filledFields}/${totalFields} fields filled.`}
          {missingRequired.length > 0 && (
            <span className="ml-auto text-pink-700 font-semibold">
              {missingRequired.length} required field{missingRequired.length === 1 ? "" : "s"} still empty
            </span>
          )}
        </div>
      </div>

      {saveModalOpen && (
        <SaveAndSubmitModal
          mode={saveMode}
          submitting={submitting}
          onCancel={() => setSaveModalOpen(false)}
          onConfirm={submitAfterSave}
        />
      )}
    </AppShell>
  );
}

// ── Save & Submit modal ──────────────────────────────────────────────
// Opens the moment the founder clicks Submit. Loads their existing
// project folders so they can drop this Launch Blueprint snapshot in
// the right place before the panel submission goes through. Creating a
// new folder inline covers the "first-time save" case.
function SaveAndSubmitModal({ mode = "submit", submitting, onCancel, onConfirm }) {
  const isDraft = mode === "draft";
  const [folders, setFolders]         = useState([]);
  const [loadingFolders, setLoading]  = useState(true);
  const [selectedFolder, setSelected] = useState("");   // "" = root
  const [name, setName]               = useState(
    `${isDraft ? "Draft" : "Launch Blueprint"} — ${new Date().toLocaleDateString()}`
  );
  const [newFolder, setNewFolder]     = useState("");
  const [creatingFolder, setCreating] = useState(false);

  useEffect(() => {
    api.get("/projects")
      .then((r) => setFolders((r.data || []).filter((p) => p.is_folder)))
      .catch(() => setFolders([]))
      .finally(() => setLoading(false));
  }, []);

  const addFolder = async () => {
    const n = newFolder.trim();
    if (!n) return;
    setCreating(true);
    try {
      const r = await api.post("/projects", { name: n, is_folder: true });
      setFolders((prev) => [r.data, ...prev]);
      setSelected(r.data.id);
      setNewFolder("");
      toast.success(`Folder "${n}" created.`);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setCreating(false); }
  };

  const confirm = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Give your Launch Blueprint a name so you can find it later.");
      return;
    }
    onConfirm({ parentId: selectedFolder || null, projectName: trimmed });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="blueprint-save-submit-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-shell-border flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-primary/10 text-primary">
            <Save className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl">
              {isDraft ? "Save draft to Projects" : "Save & Submit"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {isDraft
                ? "Save a named snapshot of your current answers to Projects. You can promote it to a panel submission later, once your active review returns a score."
                : "Save a snapshot of this Launch Blueprint to your Projects, then submit it to the CPG Institute panel. You can reopen the saved copy anytime — even after your live blueprint is edited or reopened."}
            </p>
          </div>
          <button onClick={onCancel} className="p-1 rounded hover:bg-muted" aria-label="Close" data-testid="blueprint-save-submit-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
              Save as
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary"
              data-testid="blueprint-save-name"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
              Save into folder
            </label>
            <div className="mt-2 grid gap-1 max-h-52 overflow-y-auto pr-1">
              <FolderPickRow
                testid="folder-pick-root"
                active={selectedFolder === ""}
                onClick={() => setSelected("")}
                label="No folder — save to root"
                icon={Folder}
              />
              {loadingFolders ? (
                <div className="text-xs text-muted-foreground py-2">Loading folders…</div>
              ) : folders.length === 0 ? (
                <div className="text-xs text-muted-foreground py-1">No folders yet — create one below or save to root.</div>
              ) : folders.map((f) => (
                <FolderPickRow
                  key={f.id}
                  testid={`folder-pick-${f.id}`}
                  active={selectedFolder === f.id}
                  onClick={() => setSelected(f.id)}
                  label={f.name}
                  icon={Folder}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
              Or create a new folder
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                value={newFolder}
                onChange={(e) => setNewFolder(e.target.value)}
                placeholder="e.g. Q1 launches"
                className="flex-1 rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary"
                data-testid="blueprint-new-folder-name"
              />
              <button
                onClick={addFolder}
                disabled={creatingFolder || !newFolder.trim()}
                className="inline-flex items-center gap-1 rounded-lg border border-shell-border px-3 py-2 text-xs font-semibold text-primary hover:border-primary disabled:opacity-50"
                data-testid="blueprint-create-folder-btn"
              >
                <FolderPlus className="w-4 h-4" />
                {creatingFolder ? "Creating…" : "Create folder"}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-shell-border flex items-center justify-end gap-3 bg-muted/30">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="text-sm font-semibold text-muted-foreground hover:text-foreground px-4 py-2"
            data-testid="blueprint-save-submit-cancel"
          >Cancel</button>
          <button
            onClick={confirm}
            disabled={submitting}
            className="btn-primary"
            data-testid="blueprint-save-submit-confirm"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {isDraft ? "Saving draft…" : "Saving & submitting…"}</>
            ) : isDraft ? (
              <>Save draft <Save className="w-4 h-4" /></>
            ) : (
              <>Save & submit <Send className="w-4 h-4" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function FolderPickRow({ active, onClick, label, icon: Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition ${active ? "border-primary bg-primary/5 text-primary font-semibold" : "border-transparent hover:border-shell-border"}`}
    >
      <Icon className={`w-4 h-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
      <span className="truncate flex-1">{label}</span>
      {active && <Check className="w-4 h-4 text-primary" />}
    </button>
  );
}

function BlueprintField({ field, value, onChange }) {
  if (field.type === "image") {
    return (
      <div className="block">
        <FieldLabel field={field} />
        <ImagePickerField
          value={value || ""}
          onChange={onChange}
          assetKind={field.assetKind || "logo"}
          testid={`blueprint-field-${field.id}`}
        />
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="block">
        <label className="block">
          <FieldLabel field={field} />
          <textarea rows={4} value={value} onChange={(e) => onChange(e.target.value)}
            data-testid={`blueprint-field-${field.id}`}
            className="mt-1.5 w-full rounded-xl bg-white border border-shell-border px-3 py-2.5 text-sm resize-y focus:outline-none focus:border-primary transition-colors" />
        </label>
        <FieldActions field={field} value={value} onChange={onChange} />
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className="flex items-start gap-3 cursor-pointer" data-testid={`blueprint-field-${field.id}`}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
          className="mt-1 w-4 h-4 accent-primary" />
        <span className="text-sm leading-relaxed">
          {field.label}
          {field.required && <span className="text-primary"> *</span>}
        </span>
      </label>
    );
  }
  return (
    <div className="block">
      <label className="block">
        <FieldLabel field={field} />
        <input type={field.type || "text"} value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={`blueprint-field-${field.id}`}
          className="mt-1.5 w-full rounded-xl bg-white border border-shell-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors" />
      </label>
      <FieldActions field={field} value={value} onChange={onChange} />
    </div>
  );
}

/**
 * Bottom-of-field controls: "See sample answer" disclosure + "Ask Warren AI"
 * coach trigger + the coach result panel. All owned by one component so the
 * two disclosures share a tidy layout.
 */
function FieldActions({ field, value, onChange }) {
  const [showExample, setShowExample] = useState(false);
  const [coach, setCoach] = useState({ status: "idle", critique: "", rewrite: "", error: "" });

  const showCoach =
    field.type !== "checkbox" &&
    !["founder_name", "founder_email", "founder_phone", "founder_linkedin", "founder_location"].includes(field.id);
  const answer = String(value || "").trim();
  const canAsk = answer.length >= 20 && coach.status !== "loading";

  const askWarren = async () => {
    if (!canAsk) return;
    setCoach({ status: "loading", critique: "", rewrite: "", error: "" });
    try {
      const { data } = await api.post("/blueprint/critique", {
        field_id: field.id,
        field_label: field.label,
        hint: field.hint || "",
        example: field.example || "",
        answer: answer,
      });
      setCoach({ status: "done", critique: data.critique || "", rewrite: data.rewrite || "", error: "" });
    } catch (err) {
      setCoach({ status: "error", critique: "", rewrite: "", error: formatError(err) });
    }
  };

  const applyRewrite = () => {
    if (!coach.rewrite) return;
    onChange(coach.rewrite);
    setCoach((c) => ({ ...c, status: "applied" }));
  };

  const dismissCoach = () => setCoach({ status: "idle", critique: "", rewrite: "", error: "" });

  const hasExample = !!field.example;
  if (!hasExample && !showCoach) return null;

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
        {hasExample && (
          <button
            type="button"
            onClick={() => setShowExample((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary hover:text-primary/80 transition"
            data-testid={`blueprint-example-toggle-${field.id}`}
            aria-expanded={showExample}
          >
            <Lightbulb className="w-3 h-3" />
            {showExample ? "Hide sample answer" : "See a sample answer"}
          </button>
        )}
        {showCoach && (
          <button
            type="button"
            onClick={askWarren}
            disabled={!canAsk}
            className={[
              "inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition",
              canAsk
                ? "text-shell-side hover:text-primary"
                : "text-muted-foreground/60 cursor-not-allowed",
            ].join(" ")}
            data-testid={`blueprint-coach-ask-${field.id}`}
            title={
              canAsk
                ? "Ask Warren AI to critique this answer and suggest a tighter rewrite"
                : "Type at least 20 characters, then Warren can critique it"
            }
          >
            {coach.status === "loading" ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" /> Warren is reading…
              </>
            ) : (
              <>
                <Wand2 className="w-3 h-3" /> Ask Warren AI
              </>
            )}
          </button>
        )}
      </div>

      {hasExample && showExample && (
        <div
          className="rounded-lg border-l-2 border-primary/60 bg-primary/5 px-3.5 py-2.5 text-[13px] leading-relaxed text-foreground/80 whitespace-pre-line"
          data-testid={`blueprint-example-body-${field.id}`}
        >
          <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-primary mb-1">
            Sample answer
          </span>
          {field.example}
        </div>
      )}

      {coach.status === "error" && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] text-red-800" data-testid={`blueprint-coach-error-${field.id}`}>
          {coach.error || "Warren couldn't reach that one. Try again."}
        </div>
      )}

      {(coach.status === "done" || coach.status === "applied") && (
        <div
          className="rounded-lg border border-shell-side/25 bg-shell-side/5 p-3.5 space-y-3"
          data-testid={`blueprint-coach-panel-${field.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-shell-side inline-flex items-center gap-1.5">
              <Wand2 className="w-3 h-3" /> Morgan says
            </p>
            <button
              type="button"
              onClick={dismissCoach}
              aria-label="Dismiss critique"
              className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-shell-side/10 transition"
              data-testid={`blueprint-coach-dismiss-${field.id}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-[13px] leading-relaxed text-foreground/90 whitespace-pre-line" data-testid={`blueprint-coach-critique-${field.id}`}>
            {coach.critique}
          </p>
          {coach.rewrite && (
            <div className="rounded-md border border-primary/25 bg-white p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary mb-1.5">
                Tighter rewrite
              </p>
              <p className="text-[13px] leading-relaxed whitespace-pre-line" data-testid={`blueprint-coach-rewrite-${field.id}`}>
                {coach.rewrite}
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={applyRewrite}
                  disabled={coach.status === "applied"}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] transition",
                    coach.status === "applied"
                      ? "bg-emerald-100 text-emerald-800 cursor-default"
                      : "text-white shadow hover:shadow-md",
                  ].join(" ")}
                  style={
                    coach.status === "applied"
                      ? undefined
                      : { background: "linear-gradient(135deg, #D81B60 0%, #6A0DAD 100%)" }
                  }
                  data-testid={`blueprint-coach-apply-${field.id}`}
                >
                  {coach.status === "applied" ? "Applied" : "Use this rewrite"}
                </button>
                <span className="text-[11px] text-muted-foreground">You can still edit it after applying.</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Field label + optional hover info-bubble. Matches the "?" tooltip style
 * used inside the Financial Tools calculator iframes.
 */
function FieldLabel({ field }) {
  return (
    <span className="text-sm font-semibold flex items-center gap-1.5">
      <span>
        {field.label}
        {field.required && <span className="text-primary ml-0.5">*</span>}
      </span>
      {field.hint && (
        <span
          className="blueprint-hint"
          role="tooltip"
          tabIndex={0}
          aria-label={field.hint}
          data-testid={`blueprint-hint-${field.id}`}
        >
          ?<span className="blueprint-hint-text">{field.hint}</span>
        </span>
      )}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="surface p-5">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</p>
      <p className="mt-1">{value}</p>
    </div>
  );
}

/**
 * Live 0–100 panel-score preview. Loads the last cached score on mount and
 * lets the founder trigger a fresh Warren score whenever they've made
 * meaningful edits. Gates the button to require at least 5 filled fields.
 */
function ScorePreview({ filledFields, editedCount = 0, onScoreRefreshed }) {
  const [state, setState] = useState({
    status: "idle", // "idle" | "loading" | "done" | "error"
    data: null,
    error: "",
  });
  const [expanded, setExpanded] = useState(false);
  // Delta vs the previous score after a manual refresh — surfaces briefly
  // beside the score number so the founder sees the improvement land.
  const [delta, setDelta] = useState(null);

  // Prime with the last cached score on mount so a fresh page load isn't blank.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/blueprint/score-preview");
        if (cancelled) return;
        if (data) setState({ status: "done", data, error: "" });
      } catch {
        /* silent — the preview is optional */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-fade the delta pill after 8s so it never gets in the way.
  useEffect(() => {
    if (delta === null) return;
    const t = setTimeout(() => setDelta(null), 8000);
    return () => clearTimeout(t);
  }, [delta]);

  const askWarren = async () => {
    const before = state.data?.overall_score ?? null;
    setState((s) => ({ ...s, status: "loading", error: "" }));
    try {
      const { data } = await api.post("/blueprint/score-preview");
      setState({ status: "done", data, error: "" });
      setExpanded(true);
      if (typeof before === "number" && typeof data?.overall_score === "number") {
        const d = data.overall_score - before;
        setDelta(d);
      }
      // Let the parent reset its edited-field counter so the nudge disappears.
      onScoreRefreshed?.();
    } catch (err) {
      setState((s) => ({ ...s, status: "error", error: formatError(err) }));
    }
  };

  const canAsk = filledFields >= 5 && state.status !== "loading";
  const score = state.data?.overall_score ?? null;
  const scoreTone =
    score === null
      ? "text-muted-foreground"
      : score >= 80
      ? "text-emerald-600"
      : score >= 60
      ? "text-shell-accent"
      : score >= 40
      ? "text-pink-600"
      : "text-red-600";

  return (
    <div
      className="mb-8 card-elevated overflow-hidden"
      data-testid="blueprint-score-preview"
    >
      <div
        className="p-5 md:p-6 relative"
        style={{
          background:
            "linear-gradient(135deg, rgba(216,27,96,0.05) 0%, rgba(106,13,173,0.05) 55%, rgba(30,64,175,0.05) 100%)",
        }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div
              className="w-14 h-14 shrink-0 rounded-2xl grid place-items-center text-white shadow-md"
              style={{ background: "linear-gradient(135deg, #D81B60 0%, #6A0DAD 100%)" }}
            >
              <Wand2 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
                Warren's panel score preview
              </p>
              <h3
                className="font-display text-2xl mt-1 leading-tight flex items-baseline gap-2 flex-wrap"
                style={{ fontFamily: "'Fraunces', Georgia, serif" }}
              >
                {score !== null ? (
                  <>
                    <span className={scoreTone} data-testid="blueprint-score-value">{score}</span>
                    <span className="text-muted-foreground text-xl"> / 100</span>
                    {delta !== null && delta !== 0 && (
                      <span
                        className={[
                          "text-[11px] font-bold uppercase tracking-[0.14em] rounded-full px-2 py-0.5 animate-in fade-in zoom-in-95",
                          delta > 0
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-pink-100 text-pink-800",
                        ].join(" ")}
                        data-testid="blueprint-score-delta"
                      >
                        {delta > 0 ? `+${delta}` : String(delta)} vs last
                      </span>
                    )}
                  </>
                ) : (
                  "Score your draft before you submit"
                )}
              </h3>
              {state.data?.verdict && (
                <p className="mt-1 text-sm text-foreground/80 max-w-xl" data-testid="blueprint-score-verdict">
                  {state.data.verdict}
                </p>
              )}
              {score !== null && editedCount >= 3 && state.status !== "loading" && (
                <div
                  className="mt-2 inline-flex items-center gap-2 rounded-full bg-white/70 border border-primary/25 px-3 py-1.5 text-[11.5px] font-semibold text-foreground/85 shadow-sm"
                  data-testid="blueprint-score-nudge"
                >
                  <Sparkles className="w-3 h-3 text-primary" />
                  You've edited {editedCount} field{editedCount === 1 ? "" : "s"} since your last score.
                  <button
                    type="button"
                    onClick={askWarren}
                    className="text-primary font-bold uppercase tracking-[0.12em] hover:text-primary/80"
                    data-testid="blueprint-score-nudge-refresh"
                  >
                    Refresh →
                  </button>
                </div>
              )}
              {score === null && (
                <p className="mt-1 text-sm text-muted-foreground max-w-xl">
                  Warren will read your whole draft and give you an honest 0–100 panel score, with strengths and gaps to fix before you submit.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={askWarren}
              disabled={!canAsk}
              className={[
                "inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-[0.14em] transition",
                canAsk
                  ? "text-white shadow hover:shadow-md"
                  : "bg-muted text-muted-foreground cursor-not-allowed",
              ].join(" ")}
              style={
                canAsk
                  ? { background: "linear-gradient(135deg, #D81B60 0%, #6A0DAD 100%)" }
                  : undefined
              }
              data-testid="blueprint-score-refresh"
              title={
                filledFields < 5
                  ? "Fill at least 5 fields to unlock the preview"
                  : "Get a fresh 0–100 score from Warren"
              }
            >
              {state.status === "loading" ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Scoring…
                </>
              ) : score === null ? (
                <>
                  <Wand2 className="w-3.5 h-3.5" /> Get my panel score
                </>
              ) : (
                <>
                  <Wand2 className="w-3.5 h-3.5" /> Refresh score
                </>
              )}
            </button>
            {state.data && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary hover:text-primary/80 transition"
                data-testid="blueprint-score-expand"
              >
                {expanded ? "Hide breakdown" : "See breakdown"}
              </button>
            )}
          </div>
        </div>

        {state.status === "error" && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] text-red-800">
            {state.error || "Warren couldn't score that one. Try again in a moment."}
          </div>
        )}
      </div>

      {expanded && state.data && (
        <div className="border-t border-shell-border p-5 md:p-6 space-y-5 bg-white" data-testid="blueprint-score-breakdown">
          {/* Strengths + gaps */}
          <div className="grid md:grid-cols-2 gap-4">
            {state.data.strengths?.length > 0 && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="blueprint-score-strengths">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 mb-2">
                  What's working
                </p>
                <ul className="space-y-1.5 text-[13px] leading-relaxed">
                  {state.data.strengths.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-foreground/85">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {state.data.gaps?.length > 0 && (
              <div className="rounded-xl border border-pink-200 bg-pink-50/50 p-4" data-testid="blueprint-score-gaps">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-pink-700 mb-2">
                  Fix before submit
                </p>
                <ul className="space-y-1.5 text-[13px] leading-relaxed">
                  {state.data.gaps.map((g, i) => (
                    <li key={i} className="flex items-start gap-2 text-foreground/85">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-pink-500 shrink-0" />
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Section scores */}
          {state.data.section_scores?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">
                Section scores
              </p>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="blueprint-score-sections">
                {state.data.section_scores.map((s) => {
                  const tone =
                    s.score >= 80 ? "bg-emerald-500" :
                    s.score >= 60 ? "bg-shell-accent" :
                    s.score >= 40 ? "bg-pink-500" : "bg-red-500";
                  return (
                    <div
                      key={s.id}
                      className="rounded-lg border border-shell-border bg-white px-3 py-2.5"
                      data-testid={`blueprint-score-section-${s.id}`}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-[13px] font-semibold truncate">{s.title}</p>
                        <p className="text-sm font-display font-semibold shrink-0">{s.score}</p>
                      </div>
                      <div className="mt-1.5 h-1 rounded-full bg-shell-border/70 overflow-hidden">
                        <div className={`h-full ${tone}`} style={{ width: `${s.score}%` }} />
                      </div>
                      {s.note && (
                        <p className="mt-1.5 text-[11.5px] leading-snug text-muted-foreground">
                          {s.note}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground pt-1">
            Warren's preview isn't the final panel score — it's a directional check so you can tighten weak sections before you submit for the real review.
          </p>
        </div>
      )}
    </div>
  );
}
